import type { Conversation } from '../../../lib/conversations';
import type { ChatMode } from '../../chat/ActiveConversationContext';

export function conversationLabel(conversation?: Conversation): string {
  const name = conversation?.name?.trim();
  if (name) return name;
  const created = conversation?.createdDate;
  const stamp = created ? dateTimeLabel(created) : undefined;
  return stamp ?? 'Untitled chat';
}

export function dateTimeLabel(iso: string): string | undefined {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const CHAT_MODES: ChatMode[] = ['bubble', 'panel', 'expanded', 'fullscreen'];

/**
 * The size the chat opens at on first mount, from what the URL asks for.
 *
 * A remembered size (`?chat`) always wins — it is what the user last had, and a refresh or a shared link
 * must land on exactly that. Failing that, a **handoff** or a bare **conversation deep-link** opens the side
 * dock: both arrive with a page worth seeing next to the thread.
 *
 * Returns null when the URL asks for nothing, which leaves the chat collapsed.
 */
export function initialChatMode(intent: {
  restored?: ChatMode | null;
  handoff?: boolean;
  hasConversation?: boolean;
}): ChatMode | null {
  if (intent.restored) return intent.restored;
  if (intent.handoff || intent.hasConversation) return 'expanded';
  return null;
}

export function readChatModeFromUrl(): ChatMode | null {
  if (typeof window === 'undefined') return null;
  const value = new URL(window.location.href).searchParams.get('chat');
  return value && (CHAT_MODES as string[]).includes(value) ? (value as ChatMode) : null;
}
